import React, { useState, useEffect } from 'react';
import { FaTimes, FaPaperPlane, FaReply, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import FeedAvatar from './FeedAvatar';
import postService from '../../services/postService';
import './feed.shared.css';

const CommentItem = ({ comment, allComments, onReply, user, level = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const replies = allComments.filter(c => c.parentId === comment.id);
    
    const authorName = comment.user?.profile
        ? `${comment.user.profile.firstName} ${comment.user.profile.lastName}`
        : (comment.user?.name || 'Usuario');

    return (
        <div 
            className={`comment-thread-wrapper level-${level}`}
            style={{ paddingLeft: level > 0 ? `${Math.min(level * 40, 160)}px` : '0px' }}
        >
            <div className="comment-item-main">
                <FeedAvatar name={authorName} size={level === 0 ? 32 : 24} src={comment.user?.profile?.photoUrl} />
                <div className="comment-content-container">
                    <div className="comment-bubble">
                        <span className="comment-author-name">{authorName}</span>
                        <p className="comment-text">{comment.content}</p>
                    </div>
                    <div className="comment-actions-inline">
                        <span className="comment-time-mini">{new Date(comment.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                        <button className="comment-reply-btn" onClick={() => onReply(comment)}>
                            <FaReply /> Responder
                        </button>
                        {replies.length > 0 && (
                            <button className="comment-toggle-replies" onClick={() => setIsExpanded(!isExpanded)}>
                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                {replies.length} {replies.length === 1 ? 'respuesta' : 'respuestas'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            
            {isExpanded && replies.length > 0 && (
                replies.map(reply => (
                    <CommentItem 
                        key={reply.id} 
                        comment={reply} 
                        allComments={allComments} 
                        onReply={onReply} 
                        user={user}
                        level={level + 1}
                    />
                ))
            )}
        </div>
    );
};

const CommentModal = ({ isOpen, onClose, post, user, token, onShowFeedback, onCommentAdded }) => {
    const [commentText, setCommentText] = useState('');
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);

    useEffect(() => {
        if (isOpen && post?.id) {
            loadComments();
        }
    }, [isOpen, post?.id]);

    const loadComments = async () => {
        try {
            setLoading(true);
            const data = await postService.getComments(post.id);
            setComments(data);
        } catch (err) {
            console.error('Failed to load comments:', err);
            onShowFeedback?.('Error', 'No pudimos cargar los comentarios.', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;

        try {
            const newComment = await postService.addComment(post.id, commentText, token, replyingTo?.id);
            // Ensure parentId is present in the local state update
            const commentWithParent = { ...newComment, parentId: replyingTo?.id || null };
            setComments(prev => [...prev, commentWithParent]);
            setCommentText('');
            setReplyingTo(null);
            onCommentAdded?.();
        } catch (err) {
            console.error('Failed to add comment:', err);
            onShowFeedback?.('Error', 'No pudimos publicar tu comentario.', 'error');
        }
    };

    const rootComments = comments.filter(c => c.parentId === null || c.parentId === undefined);

    const displayName = user?.profile
        ? `${user.profile.firstName} ${user.profile.lastName}`
        : (user?.name || user?.email || 'Usuario');

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content comment-modal threaded-view" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="header-titles">
                        <h3>Discusión del Foro</h3>
                        <span className="header-subtitle">{post.title || 'Tema de debate'}</span>
                    </div>
                    <button className="close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                
                <div className="modal-body">
                    <div className="comment-post-preview forum-style">
                        <div className="post-preview-header">
                            <FeedAvatar name={post.author?.name} size={30} src={post.author?.profile?.photoUrl} />
                            <strong>{post.author?.name || 'Autor'}</strong>
                        </div>
                        <p>{post.content}</p>
                    </div>

                    <div className="comments-list threaded-list">
                        {loading ? (
                            <div className="no-comments">Cargando debate...</div>
                        ) : rootComments.length > 0 ? (
                            rootComments.map(comment => (
                                <CommentItem 
                                    key={comment.id} 
                                    comment={comment} 
                                    allComments={comments} 
                                    onReply={(c) => {
                                        setReplyingTo(c);
                                        document.querySelector('.comment-form input')?.focus();
                                    }}
                                    user={user}
                                    level={0}
                                />
                            ))
                        ) : (
                            <div className="no-comments">No hay intervenciones aún. ¡Inicia el debate!</div>
                        )}
                    </div>
                </div>

                <div className="modal-footer comment-input-footer">
                    {replyingTo && (
                        <div className="replying-to-badge">
                            <span>Respondiendo a <strong>{replyingTo.user?.name || 'Usuario'}</strong></span>
                            <button onClick={() => setReplyingTo(null)}><FaTimes /></button>
                        </div>
                    )}
                    <form className="comment-form" onSubmit={handleSubmit}>
                        <FeedAvatar name={displayName} size={32} src={user?.profile?.photoUrl} />
                        <input
                            type="text"
                            placeholder={replyingTo ? "Escribe tu respuesta..." : "Añade tu intervención al foro..."}
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            autoFocus
                        />
                        <button type="submit" disabled={!commentText.trim()} className="send-comment-btn">
                            <FaPaperPlane />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CommentModal;
